import React from "react";
import "./Popular.css";

const Popular = ({
  popularCourses = [],
  popularSearches = [],
  onCourseSelect,
}) => {
  const getCourseTitle = (course) =>
    course?.post_title || course?.title || "Professional Training Course";

  const getCourseUrl = (course) => {
    if (!course) return "#";

    // Support common slug/url properties without changing your API.
    if (course.url) return course.url;
    if (course.link) return course.link;

    if (course.slug) {
      return `/courses/${course.slug}`;
    }

    return "#";
  };

  const handleCourseSelect = (course, event) => {
    // If the parent component provides selection behavior,
    // preserve the existing application functionality.
    if (onCourseSelect) {
      event.preventDefault();
      onCourseSelect(course);
    }
  };

  return (
    <section
      className="course-insights-section"
      id="popular"
      aria-labelledby="popular-courses-heading"
    >
      <div className="course-insights-container">
        <header className="course-insights-header">
          <p className="section-eyebrow">
            Popular Professional Training in Singapore
          </p>

          <h2 id="popular-courses-heading">
            Popular IT, AI, Data, Cybersecurity &amp; Corporate Training
            Courses
          </h2>

          <div className="insights-grid">
          {/* MOST VIEWED COURSES */}
          <article
            className="insight-card"
            aria-labelledby="most-viewed-heading"
          >
            <div className="insight-card-header">
              <span className="insight-label">
                Learner Interest
              </span>

              <h3 id="most-viewed-heading">
                Most Viewed Professional Training Courses
              </h3>

              <p>
                Discover training topics receiving the most course views from
                professionals and organizations.
              </p>
            </div>

            {popularCourses.length === 0 ? (
              <p className="insight-empty" aria-live="polite">
                Popular course data will appear here as visitors explore
                Trainosys training programs.
              </p>
            ) : (
              <ol className="insight-list">
                {popularCourses.slice(0, 3).map((course, index) => {
                  const title = getCourseTitle(course);
                  const url = getCourseUrl(course);

                  const clickCount =
                    course?.click_count ??
                    course?.viewCount ??
                    0;

                  return (
                    <li
                      key={course?._id || `${title}-${index}`}
                      className="insight-item"
                    >
                      <a
                        className="insight-link"
                        href={url}
                        onClick={(event) =>
                          handleCourseSelect(course, event)
                        }
                        aria-label={`View ${title} training course`}
                      >
                        <span className="insight-left">
                          <span
                            className="rank-number"
                            aria-hidden="true"
                          >
                            {index + 1}
                          </span>

                          <span className="course-title">
                            {title}
                          </span>
                        </span>

                        <span className="metric">
                          {clickCount}{" "}
                          {clickCount === 1 ? "view" : "views"}
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ol>
            )}
          </article>

          {/* MOST SEARCHED COURSES */}
          <article
            className="insight-card"
            aria-labelledby="most-searched-heading"
          >
            <div className="insight-card-header">
              <span className="insight-label">
                Search Interest
              </span>

              <h3 id="most-searched-heading">
                Most Searched IT &amp; Professional Training Topics
              </h3>

              <p>
                Explore course topics searched by professionals looking to
                develop technical, digital and business capabilities.
              </p>
            </div>

            {popularSearches.length === 0 ? (
              <p className="insight-empty" aria-live="polite">
                Search trends will appear here as visitors search the
                Trainosys training catalog.
              </p>
            ) : (
              <ol className="insight-list">
                {popularSearches.slice(0, 3).map((course, index) => {
                  const title = getCourseTitle(course);
                  const url = getCourseUrl(course);

                  const searchCount =
                    course?.search_count ??
                    course?.searchCount ??
                    0;

                  return (
                    <li
                      key={course?._id || `${title}-${index}`}
                      className="insight-item"
                    >
                      <a
                        className="insight-link"
                        href={url}
                        onClick={(event) =>
                          handleCourseSelect(course, event)
                        }
                        aria-label={`Explore ${title} training`}
                      >
                        <span className="insight-left">
                          <span
                            className="rank-number"
                            aria-hidden="true"
                          >
                            {index + 1}
                          </span>

                          <span className="course-title">
                            {title}
                          </span>
                        </span>

                        <span className="metric">
                          {searchCount}{" "}
                          {searchCount === 1
                            ? "search"
                            : "searches"}
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ol>
            )}
          </article>
        </div>

          <p className="section-intro">
            Explore the professional training courses that attract the most
            interest from learners and organizations. Trainosys provides
            practical training for technology, data, artificial intelligence,
            cybersecurity, business and workplace skills, with flexible
            delivery options for corporate and professional teams.
          </p>

          <p className="section-supporting-text">
            Looking for{" "}
            <strong>IT training in Singapore</strong>,{" "}
            <strong>AI training</strong>,{" "}
            <strong>data analytics training</strong>,{" "}
            <strong>cybersecurity training</strong>, or{" "}
            <strong>corporate training programs</strong>? Browse popular
            course topics below and select a course to view its full details.
          </p>
        </header>

      

        {/* SEARCH-INTENT SUPPORTING CONTENT */}
        <div className="training-intent-links">
          <h3>
            Explore Professional Training by Learning Need
          </h3>

          <p>
            Trainosys supports individuals and organizations looking for
            practical professional development, technology training and
            corporate learning solutions in Singapore and internationally.
          </p>

          <div className="intent-grid">
            <a href="/courses?search=IT%20Training">
              <strong>IT Training</strong>
              <span>
                Technology, software and technical skills training.
              </span>
            </a>

            <a href="/courses?search=AI%20Training">
              <strong>AI Training</strong>
              <span>
                Artificial intelligence and emerging technology skills.
              </span>
            </a>

            <a href="/courses?search=Data%20Analytics">
              <strong>Data Analytics Training</strong>
              <span>
                Data analysis, reporting and business intelligence skills.
              </span>
            </a>

            <a href="/courses?search=Cybersecurity">
              <strong>Cybersecurity Training</strong>
              <span>
                Security awareness, technical security and cyber skills.
              </span>
            </a>

            <a href="/courses?search=Corporate%20Training">
              <strong>Corporate Training</strong>
              <span>
                Practical learning programs for business teams.
              </span>
            </a>

            <a href="/courses?search=Business%20Training">
              <strong>Business Training</strong>
              <span>
                Professional and workplace capability development.
              </span>
            </a>
          </div>
        </div>

        <div className="course-insights-cta">
          <h3>
            Looking for a Specific Training Course?
          </h3>

          <p>
            Search the Trainosys training catalog to find professional
            courses suited to your technical, business or organizational
            learning requirements.
          </p>

          <a
            href="/courses"
            className="course-search-cta"
            aria-label="Search Trainosys professional training courses"
          >
            Search All Training Courses
            <span aria-hidden="true"> →</span>
          </a>
        </div>
      </div>
    </section>
  );
}

export default Popular;
